import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

interface Course {
  id: string;
  title: string;
}

interface CourseModule {
  id: string;
  title: string;
  course_id: string;
}

interface AddVideoToCourseDialogProps {
  videoId: string;
  videoTitle: string;
  videoUrl: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddVideoToCourseDialog({
  videoId,
  videoTitle,
  videoUrl,
  open,
  onOpenChange
}: AddVideoToCourseDialogProps) {
  const { toast } = useToast();
  const [courses, setCourses] = useState<Course[]>([]);
  const [modules, setModules] = useState<CourseModule[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<string>("");
  const [selectedModuleId, setSelectedModuleId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (open) {
      loadCourses();
    }
  }, [open]);

  useEffect(() => {
    if (selectedCourseId) {
      loadModules(selectedCourseId);
    } else {
      setModules([]);
      setSelectedModuleId("");
    }
  }, [selectedCourseId]);

  const loadCourses = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('id, title')
        .eq('status', 'published')
        .order('title');

      if (error) throw error;
      setCourses(data || []);
    } catch (error) {
      console.error('Error loading courses:', error);
      toast({
        title: "Error",
        description: "Failed to load courses",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadModules = async (courseId: string) => {
    try {
      const { data, error } = await supabase
        .from('course_modules')
        .select('id, title, course_id')
        .eq('course_id', courseId)
        .order('module_order');

      if (error) throw error;
      setModules(data || []);
    } catch (error) {
      console.error('Error loading modules:', error);
      toast({
        title: "Error",
        description: "Failed to load course modules",
        variant: "destructive"
      });
    }
  };

  const handleAddToCourse = async () => {
    if (!selectedModuleId) {
      toast({
        title: "Error",
        description: "Please select a module",
        variant: "destructive"
      });
      return;
    }

    setAdding(true);
    try {
      // Get the max content_order for this module
      const { data: existingContent, error: orderError } = await supabase
        .from('course_content')
        .select('content_order')
        .eq('module_id', selectedModuleId)
        .order('content_order', { ascending: false })
        .limit(1);

      if (orderError) throw orderError;

      const maxOrder = existingContent && existingContent.length > 0 
        ? existingContent[0].content_order 
        : 0;

      // Add video as course content
      const { error: insertError } = await supabase
        .from('course_content')
        .insert({
          module_id: selectedModuleId,
          title: videoTitle,
          content_type: 'video',
          content_url: videoUrl,
          content_order: maxOrder + 1,
          metadata: {
            source: 'video_library',
            video_id: videoId
          }
        });

      if (insertError) throw insertError;

      toast({
        title: "Success",
        description: "Video added to course successfully"
      });

      onOpenChange(false);
      setSelectedCourseId("");
      setSelectedModuleId("");
    } catch (error) {
      console.error('Error adding video to course:', error);
      toast({
        title: "Error",
        description: "Failed to add video to course",
        variant: "destructive"
      });
    } finally {
      setAdding(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Video to Course</DialogTitle>
          <DialogDescription>
            Select a course and module to add "{videoTitle}" to
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="course">Select Course</Label>
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <Select value={selectedCourseId} onValueChange={setSelectedCourseId}>
                <SelectTrigger id="course">
                  <SelectValue placeholder="Choose a course" />
                </SelectTrigger>
                <SelectContent>
                  {courses.map((course) => (
                    <SelectItem key={course.id} value={course.id}>
                      {course.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {selectedCourseId && (
            <div className="space-y-2">
              <Label htmlFor="module">Select Module</Label>
              <Select value={selectedModuleId} onValueChange={setSelectedModuleId}>
                <SelectTrigger id="module">
                  <SelectValue placeholder="Choose a module" />
                </SelectTrigger>
                <SelectContent>
                  {modules.map((module) => (
                    <SelectItem key={module.id} value={module.id}>
                      {module.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddToCourse} 
            disabled={!selectedModuleId || adding}
          >
            {adding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add to Course
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
